import { useJsApiLoader } from '@react-google-maps/api';
import { useEffect, useRef, useState } from 'react';
import { getGoogleMapsJsApiLoaderOptions } from 'react-components/maps/googleMapsJsApiLoader.config';
import {
  getPlaceAutocompleteElementConstructor,
  type GmpSelectAutocompleteEvent,
} from '../googlePlacesNewAutocomplete.types';

export interface UsePlacesAutocompleteParams {
  container: HTMLElement | null;
  /** Invoked when the user selects a prediction (after widget `gmp-select`). */
  onGmpSelect: (event: GmpSelectAutocompleteEvent) => void | Promise<void>;
  placeholder?: string;
}

export interface UsePlacesAutocompleteResult {
  isReady: boolean;
  loadError: Error | undefined;
  widgetUnavailable: boolean;
}

/**
 * Mounts Google `PlaceAutocompleteElement` into `container`.
 *
 * @param params - Host element and async `gmp-select` handler.
 * @returns Ready state and error flags for UI.
 */
export function usePlacesAutocomplete({
  container,
  onGmpSelect,
  placeholder = 'Search for resource',
}: UsePlacesAutocompleteParams): UsePlacesAutocompleteResult {
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsJsApiLoaderOptions());
  const [widgetUnavailable, setWidgetUnavailable] = useState(false);
  const onGmpSelectRef = useRef(onGmpSelect);
  onGmpSelectRef.current = onGmpSelect;

  useEffect(() => {
    if (!isLoaded || loadError || !container) {
      return;
    }

    const PlaceAutocompleteElement = getPlaceAutocompleteElementConstructor();
    if (!PlaceAutocompleteElement) {
      setWidgetUnavailable(true);
      return;
    }
    setWidgetUnavailable(false);

    const placeAutocomplete = new PlaceAutocompleteElement({ placeholder });
    placeAutocomplete.style.width = '100%';
    placeAutocomplete.style.boxSizing = 'border-box';

    const handleGmpSelect = (event: Event) => {
      void onGmpSelectRef.current(event as GmpSelectAutocompleteEvent);
    };

    placeAutocomplete.addEventListener('gmp-select', handleGmpSelect);
    container.appendChild(placeAutocomplete);

    return () => {
      placeAutocomplete.removeEventListener('gmp-select', handleGmpSelect);
      placeAutocomplete.remove();
      container.replaceChildren();
    };
  }, [container, isLoaded, loadError, placeholder]);

  return {
    isReady: isLoaded && !loadError && !widgetUnavailable,
    loadError,
    widgetUnavailable,
  };
}
